import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const PASS_CRITERIA_LABELS = {
  all_pass: 'All steps must pass',
  critical_only: 'No critical steps failed',
  percentage: 'Percentage threshold',
  minimum_count: 'Minimum steps passed',
};

function LevelBadge({ level }) {
  if (!level) return null;
  const cls = level === 'FF1' ? 'tr-badge tr-badge-ff1' : 'tr-badge tr-badge-ff2';
  return <span className={cls}>{level}</span>;
}

function formatTime(seconds) {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s > 0 ? `${s}s` : ''}`.trim() : `${s}s`;
}

export default function SkillDetail({ TopBar }) {
  const { id } = useParams();
  const [skill, setSkill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`/api/public/skills/${id}`)
      .then(res => {
        if (!res.ok) throw new Error('Skill not found');
        return res.json();
      })
      .then(data => { if (!cancelled) { setSkill(data); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [id]);

  const criticalCount = skill?.checklist_items?.filter(i => i.is_critical).length ?? 0;
  const timeLabel = skill ? formatTime(skill.default_time_limit_seconds) : null;
  const passLabel = skill ? (PASS_CRITERIA_LABELS[skill.pass_criteria] ?? skill.pass_criteria) : null;

  const subtitle = skill
    ? [skill.level, skill.chapter ? `Chapter ${skill.chapter}` : null].filter(Boolean).join(' · ')
    : undefined;

  return (
    <>
      <TopBar
        title={loading ? 'Loading…' : (skill?.name ?? 'Skill Not Found')}
        subtitle={subtitle}
        showBack
      />
      <div className="tr-page tr-detail-page">
        {loading && (
          <div className="tr-state-box">
            <span className="tr-state-icon">⏳</span>
            Loading…
          </div>
        )}

        {error && !loading && (
          <div className="tr-state-box">
            <span className="tr-state-icon">⚠️</span>
            {error}
          </div>
        )}

        {skill && !loading && (
          <>
            <div className="tr-detail-infostrip">
              {skill.level && <LevelBadge level={skill.level} />}
              {skill.chapter && (
                <span className="tr-detail-meta-pill">
                  Chapter <strong>{skill.chapter}</strong>
                </span>
              )}
              {passLabel && (
                <span className="tr-detail-meta-pill">
                  <span className="tr-pass-criteria">
                    {passLabel}
                    {skill.pass_criteria === 'percentage' && skill.pass_threshold
                      ? ` (${skill.pass_threshold}%)`
                      : ''}
                    {skill.pass_criteria === 'minimum_count' && skill.pass_threshold
                      ? ` (${skill.pass_threshold})`
                      : ''}
                  </span>
                </span>
              )}
              {timeLabel && (
                <span className="tr-detail-meta-pill">
                  ⏱ <strong>{timeLabel}</strong>
                </span>
              )}
              {criticalCount > 0 && (
                <span className="tr-detail-meta-pill" style={{ borderColor: '#dc2626', color: '#dc2626' }}>
                  {criticalCount} critical step{criticalCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {skill.description && (
              <div className="tr-detail-description">{skill.description}</div>
            )}

            {skill.checklist_items?.length > 0 && (
              <>
                <div className="tr-section-heading">Evaluation Steps</div>
                <div className="tr-checklist">
                  {skill.checklist_items.map((item, idx) => (
                    <div
                      key={item.id}
                      className={`tr-checklist-item${item.is_critical ? ' is-critical' : ''}`}
                    >
                      <div className="tr-checklist-item-num">{idx + 1}</div>
                      <div className="tr-checklist-item-body">
                        <div className="tr-checklist-item-text">{item.description}</div>
                        {item.is_critical && (
                          <span className="tr-critical-badge">Critical</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {(!skill.checklist_items || skill.checklist_items.length === 0) && (
              <div className="tr-state-box" style={{ paddingTop: 32 }}>
                <span className="tr-state-icon">📋</span>
                No evaluation steps defined for this skill.
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
